import { isEqual } from "lodash";
import * as React from "react";
import { useMemo, useRef } from "react";
import SVG from "react-inlinesvg";
import { animated, useSpring } from "react-spring";
import styled from "styled-components";
import AvatarIcon from "../../../assets/images/avatar_icon.svg";
import BeyondMeatTextImage from "../../../assets/images/updated_logo.svg";
import HamburgerIcon from "../../../assets/images/hamburger.svg";
import SearchIcon from "../../../assets/images/search_icon.svg";
import ShoppingCartIcon from "../../../assets/images/shopping_cart_icon.svg";
import { toREM, toVWDesktop, toVWMobile } from "../../../helpers/styles";
import { BREAKPOINTS } from "../../../helpers/theme";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { useStaticLabels } from "../../../hooks/useStaticLabels";
import { Link } from "../../shared/Link";
import { NavbarState } from "../Header";
import * as CommonHeaderTypes from "../types";
import { NavbarOptions } from "../types";
import MenuItem from "./MenuItem";
import shopify from "../../../config/shopify";
import { useSettings } from "../../../hooks/useSettings";

export enum NavBarAnimations {
  IN = "IN",
  OUT = "OUT",
}

export enum NavBarType {
  LIGHT = "LIGHT",
  DARK = "DARK",
}

export type NavBarProps = {
  type: NavBarType;
  initialHeaderBackgroundColor?: string;
  initialHeaderFontColor?: string;
  scrolledHeaderBackgroundColor?: string;
  scrolledHeaderFontColor?: string;
  transparent?: boolean;
  onMenuClick: () => void;
  onMenuItemClick: (mItem: CommonHeaderTypes.MenuItem) => void;
  menuItems: Array<CommonHeaderTypes.MenuItem>;
  selectedMenuItem: CommonHeaderTypes.MenuItem | undefined;
  animation?: NavBarAnimations;
  className?: string;
  onSearchClick: () => void;
  onAvatarClick: () => void;
  onCartClick: () => void;
  onMouseEnterSearch?: () => void;
  onMouseEnterAvatar?: () => void;
  onMouseEnterCart?: () => void;
  navState: NavbarState;
  immediate?: boolean;
  id?: string;
};

const NavBar = ({
  type = NavBarType.DARK,
  initialHeaderBackgroundColor,
  initialHeaderFontColor,
  scrolledHeaderBackgroundColor,
  scrolledHeaderFontColor,
  transparent,
  onMenuClick,
  onMenuItemClick,
  onSearchClick,
  onAvatarClick,
  onCartClick,
  onMouseEnterSearch,
  onMouseEnterAvatar,
  onMouseEnterCart,
  navState,
  menuItems,
  selectedMenuItem,
  animation,
  className,
  immediate = false,
  id,
}: NavBarProps): JSX.Element => {
  const initialBG = "#F6F1E7";
  const initialFontColor = "#012604";
  const scrollBG = "#F6F1E7";
  const scrollFontColor = "#012604";

  const staticLabels = useStaticLabels();
  const rootElement = useRef<HTMLElement | null>(null);
  const shopifyCheckout = useAppSelector(
    (state) => state.shopify.checkout,
    (a, b) =>
      a?.id === b?.id && a?.subtotalPrice?.amount === b?.subtotalPrice?.amount
  );
  const isShoppifyEnabled = useSettings();

  const { backgroundColor, fontColor } = useMemo(() => {
    if (animation === NavBarAnimations.OUT) {
      return {
        backgroundColor: transparent ? "transparent" : initialBG,
        fontColor: initialFontColor,
      };
    }
    return {
      backgroundColor: scrollBG,
      fontColor: scrollFontColor,
    };
  }, [type, transparent, initialHeaderFontColor, animation]);

  const props = useSpring({
    backgroundColor,
    fontColor,
    immediate,
  });

  return (
    <Content
      className={className}
      ref={(r) => (rootElement.current = r)}
      style={props}
      $backgroundColor={backgroundColor}
      id={id}
    >
      <DesktopRail side="left" />
      <DesktopRail side="right" />
      <MobileRail side="left" />
      <MobileRail side="right" />
      <ActionsContainer className="primary-actions">
        <BeyondMeat theme={type}>
          <Link to={"/"}>
            <BeyondMeatText
              src={BeyondMeatTextImage}
              color={fontColor}
              mobile={103}
              desktop={127}
            />
          </Link>
        </BeyondMeat>
        <MenuItemsContainer>
          {menuItems?.map((mItem, index) => (
            <React.Fragment
              key={mItem?.link ?? mItem?.label ?? `nav-${index}`}
            >
              <MenuItem
                fontColor={fontColor}
                fontFamily={`"GT Alpina"`}
                active={selectedMenuItem?.label === mItem?.label}
                onClick={(menuItem) => onMenuItemClick(menuItem)}
                onMouseEnter={(menuItem) => onMenuItemClick(menuItem)}
                menuItem={mItem}
              />
            </React.Fragment>
          ))}
        </MenuItemsContainer>
      </ActionsContainer>
      <ActionsContainer className="secondary-actions">
        {isShoppifyEnabled && (
          <>
          <NavButton
            className={navState.show === NavbarOptions.search ? "active" : ""}
            theme={type}
            onClick={onSearchClick}
            onMouseEnter={onMouseEnterSearch}
            aria-label={staticLabels?.search}
          >
            <Search
              src={SearchIcon}
              mobile={20}
              desktop={20}
              color={fontColor}
            />
          </NavButton>
          {shopify.enableAccount && (
            <NavButton
              className={navState.show === NavbarOptions.login ? "active" : ""}
              theme={type}
              onClick={onAvatarClick}
              onMouseEnter={onMouseEnterAvatar}
              desktopOnly
              aria-label={staticLabels?.account}
            >
              <Avatar
                src={AvatarIcon}
                mobile={20}
                desktop={20}
                color={fontColor}
              />
            </NavButton>
          )}
          {shopify.enableCart && (
            <CartButtonBg
              displayBadge={(shopifyCheckout?.lineItems?.length || 0) > 0}
              data-items={shopifyCheckout?.lineItems?.length || 0}
              className={navState.show === NavbarOptions.cart ? "active" : ""}
              theme={type}
              onClick={onCartClick}
              onMouseEnter={onMouseEnterCart}
              aria-label={staticLabels?.cart}
            >
              <Cart
                src={ShoppingCartIcon}
                mobile={20}
                desktop={22.5}
                color={fontColor}
              />
              </CartButtonBg>
            )}
          </>
        )}
        <MobileOnlyTransparentButton
          aria-label={"Open Menu"}
          onClick={onMenuClick}
        >
          <Hamburger
            src={HamburgerIcon}
            mobile={19}
            desktop={0}
            color={fontColor}
          />
        </MobileOnlyTransparentButton>
      </ActionsContainer>
    </Content>
  );
};

const Content = styled(animated.nav)<{ $backgroundColor: string }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.$backgroundColor};
  height: ${toVWMobile(104.5)}vw;
  padding: 0;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: ${toVWMobile(40)}vw;
    width: 100%;
    height: 1px;
    background-color: #012604;
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1px;
    background-color: #f6f1e7;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    height: auto;
    min-height: ${toVWDesktop(60)}vw;
    padding: 0 ${toVWDesktop(40)}vw;
    justify-content: space-between;
    background-color: #f6f1e7;

    &::before,
    &::after {
      display: none;
    }
  }
`;

const Icon = styled(SVG)<{ mobile: number; desktop: number; color: string }>`
  width: ${(props) => toVWMobile(props.mobile)}vw;
  height: auto;
  object-fit: contain;
  color: ${(props) => props.color};
  & path {
    fill: ${(props) => props.color};
  }
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${(props) => toVWDesktop(props.desktop)}vw;
  }
`;

const NavButton = styled.button<{
  theme: "light" | "dark";
  desktopOnly?: boolean;
}>`
  border: none;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  width: ${toVWMobile(40)}vw;
  height: ${toVWMobile(40)}vw;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: ${(p) =>
      p.theme === NavBarType.DARK ? "white" : "#75c154"};
  }

  display: none;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: flex;
    width: ${toVWDesktop(40)}vw;
    height: ${toVWDesktop(40)}vw;
  }
`;

const TransparentButton = styled.button`
  border: none;
  background-color: transparent;
  padding: 0;
  margin: 0;
`;

const MobileOnlyTransparentButton = styled(TransparentButton)`
  position: absolute;
  top: ${toVWMobile(66.5)}vw;
  right: ${toVWMobile(31)}vw;
  transform: translateY(-50%);

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
    position: static;
    transform: none;
  }
`;

const Hamburger = styled(Icon)<{ color: string }>`
  cursor: pointer;
  & line {
    stroke: ${(props) => props.color};
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

const BeyondMeat = styled.div<{ theme?: string }>`
  display: flex;
  align-items: center;
  position: absolute;
  left: 50%;
  top: ${toVWMobile(72.5)}vw;
  transform: translate(-50%, -50%);
  margin-left: 0;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    position: static;
    transform: none;
    margin-left: 0;
  }
`;

const BeyondMeatText = styled(Icon)<{ color: string }>`
  margin-left: 0;
  & path {
    fill: ${(props) => props.color};
  }

  @media (max-width: ${BREAKPOINTS.DESKTOP - 1}px) {
    width: ${toVWMobile(122.608)}vw;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-left: 0;
    width: ${toVWDesktop(102.173)}vw;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: ${toREM(12)}rem;
  width: 100%;
  justify-content: flex-end;
  padding-right: ${toVWMobile(31)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: 0;
    width: auto;
    justify-content: initial;
    padding-right: 0;
  }

  &.primary-actions {
    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      flex: 1 0 auto;
      align-items: center;
      gap: ${toVWDesktop(64)}vw;
      padding: ${toVWDesktop(15)}vw ${toVWDesktop(40)}vw;
    }
  }

  &.secondary-actions {
    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      display: none;
    }
  }
`;

const MobileRail = styled.div<{ side: "left" | "right" }>`
  position: absolute;
  top: 0;
  ${(props) => props.side}: ${toVWMobile(17.5)}vw;
  width: 1px;
  height: 100%;
  background-color: #012604;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

const Search = styled(Icon)``;
const Avatar = styled(Icon)``;
const Cart = styled(Icon)``;

const CartButtonBg = styled(NavButton)<{
  displayBadge: boolean;
}>`
  border: none;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  width: ${toVWMobile(40)}vw;
  height: ${toVWMobile(40)}vw;
  position: relative;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    cursor: pointer;
    display: flex;
    width: ${toVWDesktop(40)}vw;
    height: ${toVWDesktop(40)}vw;
  }

  & svg {
    margin-left: 0;
  }

  &:before {
    content: ${(props) => (props.displayBadge ? `attr(data-items)` : "none")};
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${toVWMobile(13)}vw;
    height: ${toVWMobile(13)}vw;
    border-radius: 50%;
    background-color: #000000;
    color: #ffffff;
    font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
    font-size: ${toREM(11)}rem;
    font-weight: 700;
    font-style: normal;
    letter-spacing: normal;
    position: absolute;
    top: 20%;

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      width: ${toVWDesktop(13)}vw;
      height: ${toVWDesktop(13)}vw;
    }
  }
`;

const MenuItemsContainer = styled.div`
  display: flex;
  margin-left: 0;
  align-items: center;
`;

const DesktopRail = styled.div<{ side: "left" | "right" }>`
  display: none;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    ${(props) => props.side}: ${toVWDesktop(40)}vw;
    width: 1px;
    background-color: #012604;
  }
`;

export default React.memo(NavBar, (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.initialHeaderBackgroundColor === nextProps.initialHeaderBackgroundColor &&
    prevProps.scrolledHeaderBackgroundColor === nextProps.scrolledHeaderBackgroundColor &&
    prevProps.initialHeaderFontColor === nextProps.initialHeaderFontColor &&
    prevProps.scrolledHeaderFontColor === nextProps.scrolledHeaderFontColor &&
    prevProps.transparent === nextProps.transparent &&
    prevProps.animation === nextProps.animation &&
    prevProps.className === nextProps.className &&
    prevProps.navState === nextProps.navState &&
    prevProps.immediate === nextProps.immediate &&
    isEqual(prevProps.selectedMenuItem, nextProps.selectedMenuItem) &&
    isEqual(prevProps.menuItems, nextProps.menuItems)
  );
});
